import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Archive,
  ArchiveRestore,
  BookMarked,
  ChevronDown,
  ChevronUp,
  Download,
  FilePlus2,
  FileText,
  Link2,
  MoreHorizontal,
  NotebookPen,
  Pin,
  PinOff,
  Paperclip,
  RotateCcw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useAuditSearch } from "./AuditSearchContext";
import {
  loadPinnedReviews,
  loadRecentReviews,
  PINNED_REVIEW_KEY,
  RECENT_SEARCH_KEY,
  removeRecentReview,
  REVIEW_HISTORY_EVENT,
} from "./classificationWorkspace";
import {
  addWorkspaceEvidence,
  addWorkspaceNote,
  canUseWorkspaceApi,
  deleteWorkspace,
  deleteWorkspaceEvidence,
  deleteWorkspaceAttachment,
  deleteWorkspaceNote,
  downloadWorkspaceAttachment,
  getWorkspace,
  linkWorkspaceMaster,
  listWorkspaces,
  migrateLocalReviews,
  restoreWorkspace,
  updateWorkspace,
  uploadWorkspaceAttachment,
  workspaceIdForQuery,
} from "./classificationWorkspaceApi";
import ClassifyReportModal from "./ClassifyReportModal";
import { fetchReportHistory, fetchReportOne } from "./reportHistoryApi";

const STATUS_LABEL = { reviewing: "검토 중", conditional: "재검토 필요", confirmed: "확정" };
const normalizeProduct = (value) => String(value || "").trim().toLocaleLowerCase().replace(/\s+/g, " ");
const formatHsk = (value) => {
  const code = String(value || "").replace(/\D/g, "");
  if (code.length >= 10) return `${code.slice(0, 4)}.${code.slice(4, 6)}-${code.slice(6, 10)}`;
  return code || "미확정";
};
const formatWhen = (value) => String(value || "").slice(0, 16).replace("T", " ");

function navigate(href) {
  window.history.pushState({}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function localFallback() {
  const pinned = new Set(loadPinnedReviews());
  return loadRecentReviews().map((query) => ({
    workspaceId: workspaceIdForQuery(query),
    title: query,
    queryText: query,
    pinned: pinned.has(query),
    archived: false,
    noteCount: 0,
    evidenceCount: 0,
    localOnly: true,
  }));
}

function WorkspaceRow({ item, active, mode, onOpen, onAction, onDetails }) {
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menu) return undefined;
    const close = (event) => {
      if (!menuRef.current?.contains(event.target)) setMenu(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);

  const action = (name) => {
    setMenu(false);
    onAction(name, item);
  };

  return (
    <div className={`group relative flex items-center gap-1 rounded-lg px-1 py-0 transition-colors ${active ? "bg-gray-200/80" : "hover:bg-gray-200/55"}`}>
      <button type="button" onClick={() => onOpen(item)} className="flex min-w-0 flex-1 items-center rounded-md px-2 py-[3px] text-left">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12px] leading-[18px] text-gray-800">{item.title}</span>
          {(item.recommendedHsk || item.noteCount > 0) && (
            <span className="mt-0.5 block truncate text-[11px] leading-4 text-[#5f6368]">
              {item.recommendedHsk ? `HSK ${item.recommendedHsk}` : ""}
              {item.recommendedHsk && item.noteCount > 0 ? " · " : ""}
              {item.noteCount > 0 ? `메모 ${item.noteCount}` : ""}
            </span>
          )}
        </span>
      </button>
      {mode === "active" && item.pinned && <Pin size={12} className="text-gray-400" />}
      <div ref={menuRef} className="relative">
        <button type="button" onClick={() => setMenu((value) => !value)} className="rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-white hover:text-gray-800 group-hover:opacity-100" title="검토 메뉴">
          <MoreHorizontal size={15} />
        </button>
        {menu && (
          <div className="absolute right-0 top-7 z-[120] w-36 rounded-xl border border-gray-200 bg-white p-1.5 text-[12px] shadow-xl">
            {mode === "active" && (
              <>
                <MenuButton icon={<NotebookPen size={13} />} label="이름 바꾸기" onClick={() => action("rename")} />
                <MenuButton icon={item.pinned ? <PinOff size={13} /> : <Pin size={13} />} label={item.pinned ? "고정 해제" : "검색 고정"} onClick={() => action("pin")} />
                <MenuButton icon={<FileText size={13} />} label={item.managed ? "품목 관리 열기" : "관리 품목으로 저장"} onClick={() => onDetails(item)} />
                <MenuButton icon={<Archive size={13} />} label="보관함으로" onClick={() => action("archive")} />
                <MenuButton danger icon={<Trash2 size={13} />} label="휴지통으로" onClick={() => action("delete")} />
              </>
            )}
            {mode === "archived" && <MenuButton icon={<ArchiveRestore size={13} />} label="검색으로 복원" onClick={() => action("unarchive")} />}
            {mode === "deleted" && <MenuButton icon={<RotateCcw size={13} />} label="복원" onClick={() => action("restore")} />}
          </div>
        )}
      </div>
    </div>
  );
}

function MenuButton({ icon, label, onClick, danger = false }) {
  return (
    <button type="button" onClick={onClick} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-gray-100 ${danger ? "text-red-600" : "text-gray-700"}`}>
      {icon}{label}
    </button>
  );
}

export function WorkspaceDialog({ workspaceId, onClose, onChanged }) {
  const [item, setItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("decision");
  const [note, setNote] = useState("");
  const [evidence, setEvidence] = useState({ type: "user", title: "", url: "" });
  const [master, setMaster] = useState({ hsk: "", composition: "", supplier: "" });
  const [attachment, setAttachment] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportModal, setReportModal] = useState(null);
  const [error, setError] = useState("");
  const attachmentRef = useRef(null);

  const reload = async () => {
    try {
      const [value, history] = await Promise.all([getWorkspace(workspaceId), fetchReportHistory()]);
      setItem(value);
      const names = new Set([normalizeProduct(value?.title), normalizeProduct(value?.queryText)].filter(Boolean));
      setReports((history || []).filter((report) => names.has(normalizeProduct(report.product_name))));
      setMaster((current) => ({ ...current, hsk: value.recommendedHsk || current.hsk }));
    } catch (e) {
      setError(e.message);
    }
  };
  useEffect(() => { reload(); }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      if (reportModal) setReportModal(null);
      else onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, reportModal]);

  const run = async (job) => {
    setBusy(true); setError("");
    try { await job(); await reload(); onChanged(); } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const openReport = async (reportId) => {
    setBusy(true); setError("");
    try {
      const value = await fetchReportOne(reportId);
      if (value?.report) setReportModal({ reportData: value.report, reportId });
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const attachments = (item?.evidence || []).filter((entry) => entry.type === "attachment");
  const sources = (item?.evidence || []).filter((entry) => entry.type !== "attachment");
  const facts = Array.isArray(item?.state?.productFacts)
    ? item.state.productFacts
    : Array.isArray(item?.state?.facts) ? item.state.facts : [];

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true" aria-label={`${item?.title || "품목"} 관리`} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="max-h-[calc(100vh-2rem)] w-full max-w-4xl overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold text-gray-400">품목 관리</div>
              <div className="mt-0.5 flex items-center gap-3">
                <h2 className="truncate text-lg font-semibold text-gray-900">{item?.title || "불러오는 중…"}</h2>
                {item && <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-600">{STATUS_LABEL[item.status] || STATUS_LABEL.reviewing}</span>}
              </div>
              {item && <div className="mt-1 font-mono text-[12px] font-semibold text-blue-700">HSK {formatHsk(item.recommendedHsk)}</div>}
            </div>
            <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"><X size={18} /></button>
          </div>
          <div className="flex gap-5 px-6 text-[12px] font-medium text-gray-500">
            {[["decision", "세번 판정"], ["notes", `분류 노트 ${item?.notes?.length || ""}`], ["sources", `근거·첨부 ${(item?.evidence || []).length || ""}`], ["reports", `보고서 ${reports.length || ""}`]].map(([key, label]) => (
              <button key={key} type="button" onClick={() => setTab(key)} className={`border-b-2 px-0.5 pb-3 ${tab === key ? "border-gray-900 text-gray-900" : "border-transparent hover:text-gray-800"}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="p-6 text-[13px]">
          {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-red-700">{error}</div>}

          {tab === "decision" && <div className="space-y-5">
            <section className="grid gap-3 sm:grid-cols-[1fr_180px]">
              <div className="rounded-xl border border-gray-200 p-4">
                <div className="text-[11px] font-semibold text-gray-400">현재 관리 세번</div>
                <div className="mt-2 font-mono text-2xl font-semibold text-gray-900">{formatHsk(item?.recommendedHsk)}</div>
                <div className="mt-2 text-[12px] text-gray-500">{item?.masterKeyword ? `마스터 세번 연결 · ${item.masterKeyword}` : "검토가 끝난 10자리 세번을 마스터에 연결할 수 있습니다."}</div>
              </div>
              <label className="rounded-xl border border-gray-200 p-4 text-[11px] font-semibold text-gray-400">판정 상태
                <select value={item?.status || "reviewing"} onChange={(e) => run(() => updateWorkspace(workspaceId, { status: e.target.value, managed: true }))} className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-2 py-2 text-[13px] font-medium text-gray-800">
                  <option value="reviewing">검토 중</option><option value="conditional">재검토 필요</option><option value="confirmed">확정</option>
                </select>
              </label>
            </section>

            <section>
              <h3 className="mb-2 font-semibold text-gray-800">상품 사실</h3>
              {facts.length ? <div className="overflow-hidden rounded-xl border border-gray-200">{facts.map((fact, index) => <div key={fact.key || index} className="grid grid-cols-[150px_1fr] border-b border-gray-100 px-4 py-2.5 last:border-0"><span className="text-gray-500">{fact.label || fact.key || `사실 ${index + 1}`}</span><span className="text-gray-800">{String(fact.value ?? fact.text ?? "")}</span></div>)}</div> : <div className="rounded-xl border border-dashed border-gray-200 px-4 py-4 text-gray-500">구조화된 상품 사실이 아직 없습니다. 분류 노트에 재질·성분·형태·용도와 미확인 사항을 기록하세요.</div>}
            </section>

            <section className="border-t border-gray-200 pt-5">
              <h3 className="mb-1 flex items-center gap-2 font-semibold text-gray-800"><BookMarked size={15} /> 마스터 세번 연동</h3>
              <p className="mb-3 text-[12px] text-gray-500">확정한 한국 HSK 10자리를 업체 마스터 세번에 반영합니다.</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <input value={master.hsk} onChange={(e) => setMaster({ ...master, hsk: e.target.value })} placeholder="한국 HSK 10자리" className="rounded-lg border border-gray-300 px-3 py-2" />
                <input value={master.composition} onChange={(e) => setMaster({ ...master, composition: e.target.value })} placeholder="주요 성분·재질" className="rounded-lg border border-gray-300 px-3 py-2" />
                <input value={master.supplier} onChange={(e) => setMaster({ ...master, supplier: e.target.value })} placeholder="공급자 (선택)" className="rounded-lg border border-gray-300 px-3 py-2" />
                <button disabled={busy || master.hsk.replace(/\D/g, "").length !== 10} onClick={() => run(() => linkWorkspaceMaster(workspaceId, { keyword: item.queryText, hsk: master.hsk, product_name: item.title, composition: master.composition, supplier: master.supplier }))} className="rounded-lg bg-gray-900 px-3 py-2 font-medium text-white disabled:opacity-40">확정 세번 등록</button>
              </div>
            </section>
          </div>}

          {tab === "notes" && <section>
            <h3 className="mb-1 flex items-center gap-2 font-semibold text-gray-800"><NotebookPen size={15} /> 분류 노트</h3>
            <p className="mb-3 text-[12px] text-gray-500">세번 판정에 필요한 상품 사실, 확인할 사항과 판정 주의사항만 기록합니다.</p>
            <div className="flex gap-2"><textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="예: 별도 직물층 접착 여부 확인 필요" className="min-w-0 flex-1 resize-y rounded-xl border border-gray-300 p-3 outline-none focus:border-gray-500" /><button disabled={busy || !note.trim()} onClick={() => run(async () => { await addWorkspaceNote(workspaceId, note); setNote(""); })} className="self-end rounded-lg bg-gray-900 px-4 py-2 text-white disabled:opacity-40">저장</button></div>
            <div className="mt-4 space-y-2">{(item?.notes || []).map((entry) => <div key={entry.noteId} className="flex gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3"><div className="min-w-0 flex-1"><p className="whitespace-pre-wrap text-gray-700">{entry.body}</p><div className="mt-1 text-[10px] text-gray-400">{formatWhen(entry.updatedAt || entry.createdAt)}</div></div><button onClick={() => run(() => deleteWorkspaceNote(workspaceId, entry.noteId))} className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button></div>)}</div>
          </section>}

          {tab === "sources" && <div className="space-y-6">
            <section>
              <h3 className="mb-1 flex items-center gap-2 font-semibold text-gray-800"><Paperclip size={15} /> 첨부파일</h3>
              <p className="mb-3 text-[12px] text-gray-500">PDF·이미지·Word·Excel·CSV·TXT, 파일당 최대 20MB. 로그인한 사용자만 내려받을 수 있으며 재배포와 분리해 보관합니다.</p>
              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3">
                <input ref={attachmentRef} type="file" onChange={(e) => setAttachment(e.target.files?.[0] || null)} className="min-w-0 flex-1 text-[12px]" accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt" />
                <button disabled={busy || !attachment} onClick={() => run(async () => { await uploadWorkspaceAttachment(workspaceId, attachment); setAttachment(null); if (attachmentRef.current) attachmentRef.current.value = ""; })} className="rounded-lg bg-gray-900 px-3 py-2 font-medium text-white disabled:opacity-40">파일 보관</button>
              </div>
              <div className="mt-3 space-y-2">{attachments.map((entry) => <div key={entry.evidenceId} className="flex items-center gap-3 rounded-xl border border-gray-200 px-3 py-2.5"><FileText size={16} className="text-gray-400" /><div className="min-w-0 flex-1"><div className="truncate font-medium text-gray-800">{entry.payload?.fileName || entry.title}</div><div className="text-[10px] text-gray-400">{entry.payload?.size ? `${Math.ceil(entry.payload.size / 1024)} KB · ` : ""}{formatWhen(entry.createdAt)}</div></div><button onClick={async () => { setBusy(true); try { await downloadWorkspaceAttachment(workspaceId, entry.evidenceId, entry.payload?.fileName || entry.title); } catch (e) { setError(e.message); } finally { setBusy(false); } }} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100" title="다운로드"><Download size={15} /></button><button onClick={() => run(() => deleteWorkspaceAttachment(workspaceId, entry.evidenceId))} className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600" title="삭제"><Trash2 size={14} /></button></div>)}</div>
            </section>

            <section className="border-t border-gray-200 pt-5">
            <h3 className="mb-2 flex items-center gap-2 font-semibold text-gray-800"><Link2 size={15} /> 근거 링크</h3>
            <div className="grid gap-2 sm:grid-cols-[140px_1fr]">
              <select value={evidence.type} onChange={(e) => setEvidence({ ...evidence, type: e.target.value })} className="rounded-lg border border-gray-300 px-2 py-2"><option value="user">사용자 자료</option><option value="manufacturer">제조사 자료</option><option value="official_ruling">공식 판정</option><option value="domestic_ruling">국내 사례</option><option value="overseas_ruling">해외 사례</option><option value="legal">법령</option><option value="other">기타</option></select>
              <input value={evidence.title} onChange={(e) => setEvidence({ ...evidence, title: e.target.value })} placeholder="근거 자료명" className="rounded-lg border border-gray-300 px-3 py-2" />
              <span /><input value={evidence.url} onChange={(e) => setEvidence({ ...evidence, url: e.target.value })} placeholder="원문 URL (선택)" className="rounded-lg border border-gray-300 px-3 py-2" />
            </div>
            <button disabled={busy || !evidence.title.trim()} onClick={() => run(async () => { await addWorkspaceEvidence(workspaceId, evidence); setEvidence({ type: "user", title: "", url: "" }); })} className="mt-2 rounded-lg border border-gray-300 px-3 py-2 font-medium hover:bg-gray-50 disabled:opacity-40">근거 추가</button>
            <div className="mt-3 space-y-2">{sources.map((entry) => <div key={entry.evidenceId} className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2"><span className="rounded bg-white px-1.5 py-0.5 text-[10px] text-gray-500">{entry.type}</span><span className="min-w-0 flex-1 truncate">{entry.url ? <a href={entry.url} target="_blank" rel="noreferrer" className="hover:underline">{entry.title}</a> : entry.title}</span><button onClick={() => run(() => deleteWorkspaceEvidence(workspaceId, entry.evidenceId))} className="text-gray-400 hover:text-red-600"><Trash2 size={13} /></button></div>)}</div>
            </section>
          </div>}

          {tab === "reports" && <section>
            <h3 className="mb-1 flex items-center gap-2 font-semibold text-gray-800"><FileText size={15} /> 보고서 이력</h3>
            <p className="mb-3 text-[12px] text-gray-500">이 품목으로 발급한 PDF·이메일 보고서를 저장 시점 그대로 다시 열 수 있습니다.</p>
            {reports.length ? <div className="overflow-hidden rounded-xl border border-gray-200">{reports.map((report) => <button type="button" key={report.report_id} onClick={() => openReport(report.report_id)} className="grid w-full grid-cols-[1fr_145px_70px_140px] items-center gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-0 hover:bg-gray-50"><span className="truncate text-gray-800">{report.product_name}</span><span className="font-mono font-semibold text-blue-700">{formatHsk(report.decided_hsk)}</span><span className="text-gray-500">{report.issued_via === "email" ? "이메일" : "PDF"}</span><span className="text-[11px] text-gray-400">{formatWhen(report.updated_at || report.created_at)}</span></button>)}</div> : <div className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-gray-500">아직 발급한 보고서가 없습니다.</div>}
          </section>
          }
        </div>
        <div className="sticky bottom-0 z-10 flex justify-end border-t border-gray-200 bg-white/95 px-6 py-3 backdrop-blur">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-[13px] font-medium text-gray-700 hover:bg-gray-50">닫기</button>
        </div>
      </div>
      {reportModal && <ClassifyReportModal reportData={reportModal.reportData} reportId={reportModal.reportId} onClose={() => { setReportModal(null); reload(); }} />}
    </div>,
    document.body,
  );
}

export default function ClassificationWorkspaceNav() {
  const { searchContext, setSearchContext, setSearchResults, setFocusedCase, setGoogleImageQ, setPendingSearch } = useAuditSearch();
  const [items, setItems] = useState([]);
  const [view, setView] = useState("active");
  const [detailsId, setDetailsId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [managementOpen, setManagementOpen] = useState(false);
  // 기록 칸 접기(기억) · 최근 검색은 5개만 먼저
  const [folded, setFolded] = useState(() => { try { return JSON.parse(localStorage.getItem("loginhs:nav-folded") || "{}"); } catch (_) { return {}; } });
  const toggleFold = (key) => setFolded((prev) => { const next = { ...prev, [key]: !prev[key] }; try { localStorage.setItem("loginhs:nav-folded", JSON.stringify(next)); } catch (_) { /* ignore */ } return next; });
  const [showAllRecent, setShowAllRecent] = useState(false);
  const [filterText, setFilterText] = useState("");   // 지난 검색 거르기(이름·검색어·세번)
  const RECENT_LIMIT = 5;
  const managementRef = useRef(null);

  useEffect(() => {
    if (!managementOpen) return undefined;
    const close = (event) => {
      if (!managementRef.current?.contains(event.target)) setManagementOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [managementOpen]);

  // 갱신은 한 번에 하나만 돈다. 진행 중에 또 요청되면 끝난 뒤 한 번만 더 돈다.
  // (검색·다른 탭의 storage 이벤트가 겹칠 때 서버에 같은 목록 요청이 수십 건 몰리던 것을 막는다.)
  const refreshingRef = useRef(false);
  const pendingRef = useRef(false);
  const debounceRef = useRef(null);

  const refresh = async () => {
    if (!canUseWorkspaceApi()) { setItems(localFallback()); setLoading(false); return; }
    if (refreshingRef.current) { pendingRef.current = true; return; }
    refreshingRef.current = true;
    setLoading(true);
    try {
      await migrateLocalReviews(loadRecentReviews(), loadPinnedReviews());
      const options = view === "archived" ? { archived: true } : view === "deleted" ? { deleted: true } : {};
      const data = await listWorkspaces(options);
      setItems(data.items || []);
    } catch { setItems(view === "active" ? localFallback() : []); }
    finally {
      setLoading(false);
      refreshingRef.current = false;
      if (pendingRef.current) { pendingRef.current = false; refresh(); }
    }
  };

  const refreshSoon = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { debounceRef.current = null; refresh(); }, 600);
  };

  useEffect(() => { refresh(); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handler = (event) => {
      if (!event.key || event.key === RECENT_SEARCH_KEY || event.key === PINNED_REVIEW_KEY) refreshSoon();
    };
    window.addEventListener("storage", handler);
    window.addEventListener(REVIEW_HISTORY_EVENT, handler);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener(REVIEW_HISTORY_EVENT, handler);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentQuery = String(searchContext?.q || "").trim();
  const pinned = useMemo(() => items.filter((item) => item.pinned), [items]);
  const managed = useMemo(() => items.filter((item) => item.managed && !item.pinned), [items]);
  const recent = useMemo(() => items.filter((item) => !item.pinned && !item.managed), [items]);

  const open = (item) => {
    if (view !== "active") return;
    setPendingSearch({ q: item.queryText, ts: Date.now(), mode: item.activeMode || "auto", workspaceId: item.workspaceId });
  };

  const act = async (name, item) => {
    try {
      if (item.localOnly) {
        if (name === "delete") removeRecentReview(item.queryText);
        return refresh();
      }
      if (name === "rename") {
        const title = window.prompt("검색 이름", item.title);
        if (title?.trim()) await updateWorkspace(item.workspaceId, { title: title.trim() });
      } else if (name === "pin") await updateWorkspace(item.workspaceId, { pinned: !item.pinned });
      else if (name === "archive") await updateWorkspace(item.workspaceId, { archived: true, pinned: false });
      else if (name === "unarchive") await updateWorkspace(item.workspaceId, { archived: false });
      else if (name === "delete") { await deleteWorkspace(item.workspaceId); removeRecentReview(item.queryText); }
      else if (name === "restore") await restoreWorkspace(item.workspaceId);
      await refresh();
    } catch (error) { window.alert(error.message || "작업을 완료하지 못했습니다."); }
  };

  const startNewReview = () => {
    setSearchContext(null); setSearchResults(null); setFocusedCase(null); setGoogleImageQ(null); setPendingSearch(null);
    window.dispatchEvent(new CustomEvent("loginhs:new-review"));
    navigate("/");
  };

  const openDetails = async (item) => {
    if (item.localOnly) return;
    try {
      if (!item.managed) await updateWorkspace(item.workspaceId, { managed: true });
      setDetailsId(item.workspaceId);
      await refresh();
    } catch (error) {
      window.alert(error.message || "품목 관리 화면을 열지 못했습니다.");
    }
  };

  const selectManagementView = (nextView) => {
    setManagementOpen(false);
    setView(nextView);
  };

  const renderRows = (rows) => rows.map((item) => <WorkspaceRow key={item.workspaceId} item={item} active={item.queryText === currentQuery} mode={view} onOpen={open} onAction={act} onDetails={openDetails} />);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-1 pb-2">
        {view === "active" && managed.length > 0 && <section className="mb-2"><SectionTitle icon={<BookMarked size={12} />} title="관리 품목" count={managed.length} folded={folded.managed} onToggle={() => toggleFold("managed")} />{!folded.managed && renderRows(managed)}</section>}
        {view === "active" && pinned.length > 0 && <section className="mb-2"><SectionTitle icon={<Pin size={12} />} title="고정한 검색" count={pinned.length} folded={folded.pinned} onToggle={() => toggleFold("pinned")} />{!folded.pinned && renderRows(pinned)}</section>}
        <section>
          <SectionTitle
            icon={view === "active" ? <Search size={12} /> : view === "archived" ? <Archive size={12} /> : <Trash2 size={12} />}
            title={view === "active" ? "최근 검색" : view === "archived" ? "검색 보관함" : "삭제한 검색"}
            count={view === "active" ? recent.length : null}
            folded={view === "active" && folded.recent}
            onToggle={view === "active" ? () => toggleFold("recent") : undefined}
            action={view === "active" ? (
              <button type="button" onClick={startNewReview} title="검색 화면을 비우고 처음부터" data-testid="nav-new-search"
                className="ml-auto flex flex-none items-center gap-1 whitespace-nowrap rounded-md border border-[#cfe0f7] bg-[#f4f8fe] px-1.5 py-0 text-[11px] font-semibold leading-[18px] text-[#1f5fbf] hover:bg-[#e8f0fc]">
                <FilePlus2 size={12} strokeWidth={1.8} /> 새 검색
              </button>
            ) : null}
          />
          {!(view === "active" && folded.recent) && (view === "active" ? recent : items).length > RECENT_LIMIT && (
            <div className="px-1.5 pb-1">
              <input
                type="search"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="지난 검색 찾기"
                aria-label="지난 검색 찾기"
                data-testid="nav-search-filter"
                className="w-full rounded-md border border-gray-200 bg-white px-2 py-[3px] text-[12px] leading-[18px] text-gray-800 placeholder:text-gray-400 focus:border-[#9dbbe8] focus:outline-none"
              />
            </div>
          )}
          {view === "active" && folded.recent ? null : loading ? <div className="px-2 py-3 text-[12px] text-[#5f6368]">불러오는 중…</div> : (view === "active" ? recent : items).length > 0 ? (
            <>
              {(() => {
                const base = view === "active" ? recent : items;
                const f = filterText.trim().toLowerCase();
                if (f) {
                  const hit = base.filter((it) => [it.title, it.queryText, it.recommendedHsk].some((v) => String(v || "").toLowerCase().includes(f)));
                  return hit.length ? renderRows(hit) : <div className="px-2 py-2 text-[12px] text-[#5f6368]">'{filterText.trim()}' 이(가) 들어간 검색이 없습니다.</div>;
                }
                return renderRows(view === "active" && !showAllRecent ? base.slice(0, RECENT_LIMIT) : base);
              })()}
              {view === "active" && !filterText.trim() && recent.length > RECENT_LIMIT && (
                <button type="button" onClick={() => setShowAllRecent((v) => !v)} className="ml-2 mt-0.5 rounded px-1.5 py-0.5 text-[11px] text-[#1f5fbf] hover:bg-gray-200/55">
                  {showAllRecent ? "접기" : `더 보기 (${recent.length - RECENT_LIMIT})`}
                </button>
              )}
            </>
          ) : <div className="px-2 py-3 text-[12px] leading-5 text-[#5f6368]">{view === "active" ? "검색한 품목이 여기에 자동 저장됩니다." : "보관된 품목이 없습니다."}</div>}
        </section>
      </div>
      <div ref={managementRef} className="relative border-t border-gray-200 px-1 py-2">
        <button
          type="button"
          onClick={() => setManagementOpen((value) => !value)}
          className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[12px] leading-5 transition-colors hover:bg-gray-200/60 hover:text-gray-900 ${view === "active" ? "text-gray-600" : "bg-gray-200/55 font-medium text-gray-900"}`}
          aria-expanded={managementOpen}
        >
          <Archive size={16} strokeWidth={1.65} />
          <span className="flex-1 text-left">{view === "active" ? "검색 관리" : view === "archived" ? "검색 보관함" : "삭제한 검색"}</span>
          {managementOpen ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        </button>
        {managementOpen && (
          <div className="absolute bottom-[calc(100%-4px)] left-1 right-1 z-[130] rounded-xl border border-gray-200 bg-white p-1.5 text-[12px] shadow-xl">
            {view !== "active" && <MenuButton icon={<Search size={14} />} label="최근 검색" onClick={() => selectManagementView("active")} />}
            <MenuButton icon={<Archive size={14} />} label="검색 보관함" onClick={() => selectManagementView("archived")} />
            <MenuButton icon={<Trash2 size={14} />} label="삭제한 검색" onClick={() => selectManagementView("deleted")} />
            <div className="my-1 border-t border-gray-100" />
            <MenuButton icon={<FileText size={14} />} label="품목 관리" onClick={() => { setManagementOpen(false); navigate("/?audit=my-reports"); }} />
          </div>
        )}
      </div>
      {detailsId && <WorkspaceDialog workspaceId={detailsId} onClose={() => setDetailsId(null)} onChanged={refresh} />}
    </div>
  );
}

function SectionTitle({ icon, title, count = null, folded = false, onToggle, action = null }) {
  const label = <>{icon}{title}{count != null && count > 0 && <span className="font-normal text-[#80868b]">{count}</span>}{onToggle && <span aria-hidden="true" className="font-normal text-[#9aa0a6]">{folded ? "▸" : "▾"}</span>}</>;
  return (
    <div className="flex items-center gap-1.5 px-2 pb-1 pt-2 text-[11px] leading-[17px] font-semibold text-[#5f6368]">
      {onToggle
        ? <button type="button" onClick={onToggle} aria-expanded={!folded} className="flex min-w-0 items-center gap-1.5 whitespace-nowrap rounded hover:text-gray-900">{label}</button>
        : <span className="flex min-w-0 items-center gap-1.5 whitespace-nowrap">{label}</span>}
      {action}
    </div>
  );
}
